import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey);

// --------------- Settings ---------------

/**
 * Fetch company settings from Supabase.
 * The settings table contains exactly one row.
 */
export async function fetchSettingsSupabase() {
  const { data, error } = await supabase
    .from("settings")
    .select("company_name, tagline, phone, email, website, address, instagram, facebook, linkedin, about")
    .single();

  if (error) throw error;
  return data;
}

// --------------- Projects ---------------

/**
 * Fetch all projects from Supabase, ordered by display_order ASC.
 * Maps fields to match the shape expected by existing React components.
 */
export async function fetchProjectsSupabase() {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name, client, location, sector, status, display_order, featured")
    .order("display_order", { ascending: true });

  if (error) throw error;

  return (data || []).map((p) => ({
    ...p,
    group: "current",
    order: p.display_order,
  }));
}

/**
 * Fetch featured projects from Supabase (featured = true), ordered by display_order ASC.
 */
export async function fetchFeaturedProjectsSupabase() {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name, client, location, sector, status, display_order, featured")
    .eq("featured", true)
    .order("display_order", { ascending: true });

  if (error) throw error;

  return (data || []).map((p) => ({
    ...p,
    group: "current",
    order: p.display_order,
  }));
}


/**
 * Fetch project statistics from Supabase.
 * Returns { ongoing, completed, legacy, total }.
 */
export async function fetchStatsSupabase() {
  const [totalRes, ongoingRes, completedRes] = await Promise.all([
    supabase.from("projects").select("*", { count: "exact", head: true }),
    supabase.from("projects").select("*", { count: "exact", head: true }).eq("status", "ongoing"),
    supabase.from("projects").select("*", { count: "exact", head: true }).eq("status", "completed"),
  ]);

  if (totalRes.error) throw totalRes.error;
  if (ongoingRes.error) throw ongoingRes.error;
  if (completedRes.error) throw completedRes.error;

  return {
    total: totalRes.count || 0,
    ongoing: ongoingRes.count || 0,
    completed: completedRes.count || 0,
    legacy: 0,
  };
}

/**
 * Fetch all projects for the admin dashboard, ordered by display_order ASC.
 */
export async function fetchProjectsAdminSupabase() {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name, client, location, sector, status, display_order, featured")
    .order("display_order", { ascending: true });

  if (error) throw error;

  return (data || []).map((p) => ({
    ...p,
    group: "current",
    order: p.display_order,
  }));
}

/**
 * Create a new project row.
 */
export async function createProjectSupabase(project) {
  // Determine next display_order
  const { data: maxRow } = await supabase
    .from("projects")
    .select("display_order")
    .order("display_order", { ascending: false })
    .limit(1);

  const nextOrder = (maxRow && maxRow.length > 0 ? maxRow[0].display_order : 0) + 1;

  const { data, error } = await supabase
    .from("projects")
    .insert({
      name: project.name || "",
      client: project.client || "",
      location: project.location || "",
      sector: project.sector || "General",
      status: project.status || "completed",
      display_order: nextOrder,
      featured: project.featured ?? false,
    })
    .select()
    .single();

  if (error) throw error;

  return { ...data, group: "current", order: data.display_order };
}

/**
 * Update an existing project row.
 * Only passes through columns that exist in the Supabase table.
 */
export async function updateProjectSupabase(id, updates) {
  const payload = {};
  if (updates.name !== undefined) payload.name = updates.name;
  if (updates.client !== undefined) payload.client = updates.client;
  if (updates.location !== undefined) payload.location = updates.location;
  if (updates.sector !== undefined) payload.sector = updates.sector;
  if (updates.status !== undefined) payload.status = updates.status;
  if (updates.display_order !== undefined) payload.display_order = updates.display_order;
  if (updates.featured !== undefined) payload.featured = updates.featured;

  const { data, error } = await supabase
    .from("projects")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;

  return { ...data, group: "current", order: data.display_order };
}


/**
 * Delete a project row.
 */
export async function deleteProjectSupabase(id) {
  const { error } = await supabase
    .from("projects")
    .delete()
    .eq("id", id);

  if (error) throw error;

  return { message: "Project deleted" };
}

// --------------- Gallery ---------------

/**
 * Map a Supabase gallery row to the shape the existing UI components expect.
 */
function mapGalleryRow(row) {
  return {
    id: row.id,
    category: row.project_name || "General",
    caption: row.caption || "",
    order: row.display_order,
    image_url: row.image_url || "",
  };
}

/**
 * Fetch all gallery items from Supabase, ordered by display_order ASC.
 */
export async function fetchGallerySupabase() {
  const { data, error } = await supabase
    .from("gallery")
    .select("id, image_url, project_name, caption, display_order")
    .order("display_order", { ascending: true });

  if (error) throw error;
  return (data || []).map(mapGalleryRow);
}

/**
 * Return the public image URL for a gallery item.
 * If the value is already a full URL, return it directly.
 */
export function galleryImageUrlSupabase(imageUrl) {
  if (!imageUrl) return "";
  // Already a full URL (Supabase public URL or blob)
  if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) return imageUrl;
  // Relative path inside the storage bucket — build public URL
  const { data } = supabase.storage.from("images").getPublicUrl(imageUrl);
  return data?.publicUrl || imageUrl;
}

/**
 * Upload a single image to Supabase Storage and insert a gallery row.
 */
export async function uploadGalleryImageSupabase(file, projectName, caption = "") {
  // Generate a unique file path
  const ext = file.name.split(".").pop() || "jpg";
  const filePath = `gallery/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

  // 1. Upload to Storage bucket "images"
  const { error: uploadError } = await supabase.storage
    .from("images")
    .upload(filePath, file, { contentType: file.type });

  if (uploadError) throw uploadError;

  // 2. Get public URL
  const { data: urlData } = supabase.storage.from("images").getPublicUrl(filePath);
  const publicUrl = urlData?.publicUrl || "";

  // 3. Determine next display_order
  const { data: maxRow } = await supabase
    .from("gallery")
    .select("display_order")
    .order("display_order", { ascending: false })
    .limit(1);

  const nextOrder = (maxRow && maxRow.length > 0 ? maxRow[0].display_order : 0) + 1;

  // 4. Insert row
  const { data: inserted, error: insertError } = await supabase
    .from("gallery")
    .insert({
      image_url: publicUrl,
      project_name: projectName || "General",
      caption: caption || "",
      display_order: nextOrder,
    })
    .select()
    .single();

  if (insertError) throw insertError;
  return mapGalleryRow(inserted);
}

/**
 * Update gallery metadata (project_name, caption, display_order only).
 */
export async function updateGalleryImageSupabase(id, updates) {
  const payload = {};
  if (updates.category !== undefined) payload.project_name = updates.category;
  if (updates.caption !== undefined) payload.caption = updates.caption;
  if (updates.display_order !== undefined) payload.display_order = updates.display_order;

  const { data, error } = await supabase
    .from("gallery")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return mapGalleryRow(data);
}

/**
 * Delete a gallery row and its image from Storage.
 */
export async function deleteGalleryImageSupabase(id) {
  // 1. Get the image_url so we can delete from storage
  const { data: row, error: fetchErr } = await supabase
    .from("gallery")
    .select("image_url")
    .eq("id", id)
    .single();

  if (fetchErr) throw fetchErr;

  // 2. Delete the row
  const { error: deleteErr } = await supabase
    .from("gallery")
    .delete()
    .eq("id", id);

  if (deleteErr) throw deleteErr;

  // 3. Delete the file from storage (best-effort)
  if (row?.image_url) {
    try {
      // Extract the storage path from the public URL
      const url = row.image_url;
      const bucketSegment = "/storage/v1/object/public/images/";
      const idx = url.indexOf(bucketSegment);
      if (idx !== -1) {
        const storagePath = decodeURIComponent(url.substring(idx + bucketSegment.length));
        await supabase.storage.from("images").remove([storagePath]);
      }
    } catch (e) {
      console.warn("Could not delete image from storage:", e);
    }
  }

  return { message: "Image deleted" };
}

